/**
 * Inventory Store
 *
 * Manages inventory-related state including delivery orders, dispensing, etc.
 */

import * as React from "react";
import deliveryOrderService from "@/services/deliveryOrder";
import { createStore } from "@/stores/core/utils";
import type { BaseStore } from "@/stores/types";

// ==================== Types ====================

interface InventoryState {
  // Delivery Orders
  deliveryOrdersTotal: number;

  // Future: Add other inventory totals as needed
  // dispensingTotal: number;
  // adjustmentTotal: number;
  // requisitionTotal: number;
}

interface InventoryActions {
  // Delivery Orders
  setDeliveryOrdersTotal: (total: number) => void;

  // Reset actions
  resetInventoryState: () => void;
}

type InventoryStore = BaseStore & InventoryState & InventoryActions;

// ==================== Initial State ====================

const initialState: InventoryState = {
  deliveryOrdersTotal: 0,
};

// ==================== Store Definition ====================
// Store with persistence and environment isolation

const useInventoryStore = createStore<InventoryStore>(
  (set) => ({
    ...initialState,

    // Delivery Orders
    setDeliveryOrdersTotal: (total: number) => set({ deliveryOrdersTotal: total }),

    // Reset
    resetInventoryState: () => set(initialState),
  }),
  {
    name: "inventory-store",
    persist: true,
    version: 1,
    // Only persist the state data, not the actions
    partialize: (state) => ({
      deliveryOrdersTotal: state.deliveryOrdersTotal,
    }),
  },
);

// ==================== Selectors ====================

export const useInventoryState = () => useInventoryStore();

export const useDeliveryOrdersTotal = () => useInventoryStore((state) => state.deliveryOrdersTotal);

// Export individual actions to avoid object reference issues
export const useSetDeliveryOrdersTotal = () =>
  useInventoryStore((state) => state.setDeliveryOrdersTotal);

export const useResetInventoryState = () => useInventoryStore((state) => state.resetInventoryState);

// Backward compatible actions object (optimized with useMemo)
export const useInventoryActions = () => {
  const setDeliveryOrdersTotal = useInventoryStore((state) => state.setDeliveryOrdersTotal);
  const resetInventoryState = useInventoryStore((state) => state.resetInventoryState);

  // Use React's useMemo to cache the object and avoid infinite loops
  return React.useMemo(
    () => ({
      setDeliveryOrdersTotal,
      resetInventoryState,
    }),
    [setDeliveryOrdersTotal, resetInventoryState],
  );
};

export const withDraftCountRefresh = async <T>(operation: () => Promise<T>): Promise<T> => {
  try {
    // Execute the main operation
    const result = await operation();

    // After successful operation, refresh the draft count
    const { setDeliveryOrdersTotal } = useInventoryStore.getState();
    deliveryOrderService.getDraftDeliveryOrdersCount().then((res) => {
      setDeliveryOrdersTotal(res.data);
    });
    return result;
  } catch (error) {
    // Re-throw the error to be handled by the caller
    throw error;
  }
};

// ==================== Exports ====================

export { useInventoryStore };
export type { InventoryState, InventoryActions, InventoryStore };
